import { useRef } from 'react';

import { useParams } from '@tanstack/react-router';

import { client } from '@/api/generated/client.gen';
import { contactsApi } from '@/api/queries/contacts';
import { withForm } from '@/components/form';
import { Button, Icon, IconButton, SectionCard, Tag, useToast } from '@/components/ui';

import type { JobApplicationFormItem } from '../../contactSchema';
import { defaultFormValues } from '../../contactSchema';
import { AddItemButton, RepeaterItem } from './RepeaterItem';

const empty = (): JobApplicationFormItem => ({ attachments: [] });

export const JobApplicationsSection = withForm({
  defaultValues: defaultFormValues,
  render: function JobApplicationsSection({ form }) {
    const params = useParams({ strict: false }) as { id?: string };
    const contactId = params.id;
    const toast = useToast();
    const upload = contactsApi.useUploadAttachment();
    const removeAttachment = contactsApi.useDeleteAttachment();
    const fileRef = useRef<HTMLInputElement>(null);

    // Job-application files go through the shared attachment endpoint, so they
    // land in contact.attachments too (and show in the Attachments section). We
    // mirror them into the top-level form field so the full-object PUT round-trips.
    const handleUpload = async (i: number, files: FileList | null) => {
      if (!files || !contactId) return;
      for (const file of Array.from(files)) {
        try {
          const att = await upload.mutateAsync({ id: contactId, file, category: 'jobApplication' });
          form.pushFieldValue(`jobApplications[${i}].attachments`, att as never);
          form.pushFieldValue('attachments', att as never);
        } catch {
          toast.show({ title: `Failed to upload ${file.name}`, severity: 'error' });
        }
      }
      if (fileRef.current) fileRef.current.value = '';
    };

    const handleRemoveAttachment = async (i: number, attIndex: number, fileId?: string) => {
      if (contactId && fileId) {
        try {
          await removeAttachment.mutateAsync({ id: contactId, fileId });
        } catch {
          toast.show({ title: 'Failed to remove attachment', severity: 'error' });
          return;
        }
      }
      form.removeFieldValue(`jobApplications[${i}].attachments`, attIndex);
      // Drop the mirrored copy from contact.attachments too.
      const top = form.state.values.attachments ?? [];
      const topIdx = top.findIndex((a) => a.fileId === fileId);
      if (fileId && topIdx >= 0) form.removeFieldValue('attachments', topIdx);
    };

    const handleDownload = async (fileId?: string, name?: string) => {
      if (!contactId || !fileId) return;
      try {
        const res = await client.instance.get(`/contacts/${contactId}/attachments/${fileId}`, {
          responseType: 'blob',
        });
        const url = URL.createObjectURL(res.data as Blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = name || fileId;
        a.click();
        URL.revokeObjectURL(url);
      } catch {
        toast.show({ title: 'Could not open attachment', severity: 'error' });
      }
    };

    return (
      <SectionCard
        title="Job Applications"
        actions={<AddItemButton onClick={() => form.pushFieldValue('jobApplications', empty())} />}
      >
        <form.AppField name="jobApplications">
          {(field) => (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {(field.state.value ?? []).map((entry, i) => (
                <RepeaterItem key={i} onRemove={() => field.removeValue(i)} removeLabel="Remove job application">
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <div className="ds-form-grid-3">
                      <form.AppField name={`jobApplications[${i}].date`}>
                        {(f) => <f.DatePicker label="Date" />}
                      </form.AppField>
                      <form.AppField name={`jobApplications[${i}].portal`}>
                        {(f) => <f.TextField label="Portal" />}
                      </form.AppField>
                      <form.AppField name={`jobApplications[${i}].jobId`}>
                        {(f) => <f.TextField label="Job ID" />}
                      </form.AppField>
                    </div>
                    <div className="ds-form-grid-2">
                      <form.AppField name={`jobApplications[${i}].jobTitle`}>
                        {(f) => <f.TextField label="Job title" />}
                      </form.AppField>
                      <form.AppField name={`jobApplications[${i}].institutionName`}>
                        {(f) => <f.TextField label="Institution name" />}
                      </form.AppField>
                    </div>
                    <form.AppField name={`jobApplications[${i}].jobText`}>
                      {(f) => <f.TextField label="Job text" multiline rows={3} />}
                    </form.AppField>
                    <form.AppField name={`jobApplications[${i}].message`}>
                      {(f) => <f.TextField label="Message" multiline rows={3} />}
                    </form.AppField>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <span style={{ fontSize: 12, color: 'var(--text-2)', fontWeight: 500 }}>
                          Attachments
                        </span>
                        <input
                          ref={i === 0 ? fileRef : undefined}
                          type="file"
                          multiple
                          style={{ display: 'none' }}
                          id={`ja-file-${i}`}
                          onChange={(e) => void handleUpload(i, e.target.files)}
                        />
                        <Button
                          variant="ghost"
                          size="sm"
                          leftIcon={<Icon name="upload" size={12} />}
                          loading={upload.isPending}
                          disabled={!contactId || upload.isPending}
                          onClick={() => document.getElementById(`ja-file-${i}`)?.click()}
                        >
                          Upload
                        </Button>
                      </div>
                      {!contactId && (
                        <div style={{ fontSize: 11.5, color: 'var(--text-3)' }}>
                          Save the contact first, then you can upload files.
                        </div>
                      )}
                      {(entry.attachments ?? []).map((a, ai) => (
                        <div
                          key={a.fileId || ai}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                            padding: 8,
                            border: '1px solid var(--divider)',
                            borderRadius: 'var(--radius-sm)',
                            background: 'var(--surface)',
                          }}
                        >
                          <Icon name="note" size={14} style={{ color: 'var(--text-3)' }} />
                          <span
                            style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                            title={a.name}
                          >
                            {a.name || a.fileId}
                          </span>
                          {a.category && <Tag>{a.category}</Tag>}
                          <IconButton
                            icon="download"
                            iconSize={12}
                            size="sm"
                            label="Download attachment"
                            onClick={() => void handleDownload(a.fileId, a.name)}
                          />
                          <IconButton
                            icon="trash"
                            iconSize={12}
                            size="sm"
                            variant="danger"
                            label="Remove attachment"
                            onClick={() => void handleRemoveAttachment(i, ai, a.fileId)}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                </RepeaterItem>
              ))}
            </div>
          )}
        </form.AppField>
      </SectionCard>
    );
  },
});
